import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ContextDevTool } from 'react-context-devtool';
import useRemote from './useRemote';
import DataSubscriptionUpdater from './RemoteDataSubscriptionUpdater';
import { RemoteDataContext } from './RemoteDataContext';

function RemoteDataProvider({ children, email, isProd }) {
  const [state, { selectors, operations, global }] = useRemote();

  const { isLoading, isOffline } = selectors.getValues().reduce(
    (acc, { loading, offline }) => {
      if (loading) {
        acc.isLoading = true;
      }
      if (offline) {
        acc.isOffline = true;
      }

      return acc;
    },
    { isLoading: false, isOffline: false },
  );

  const updateOnlineOfflineStatus = useCallback(() => {
    const isOnline = navigator.onLine;
    if (isOnline) {
      return global.setOnline();
    }

    return global.setOffline();
  });

  const onError = useCallback(() => {
    global.invalidateAllRemoteData();
  });

  useEffect(() => {
    window.addEventListener('online', updateOnlineOfflineStatus);
    window.addEventListener('offline', updateOnlineOfflineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineOfflineStatus);
      window.removeEventListener('offline', updateOnlineOfflineStatus);
    };
  }, []);

  return (
    <RemoteDataContext.Provider
      value={{
        loading: isLoading || !email,
        offline: isOffline,
        remoteData: state,
        // DO NOT EXPOSE ANY SETTER
        selectors,
        operations,
        global: {
          emptyData: global.emptyData,
        },
      }}
    >
      {!isProd && <ContextDevTool context={RemoteDataContext} id="RemoteDataContext" displayName="RemoteDataContext" />}
      {typeof email === 'string' && (
        <DataSubscriptionUpdater
          onUpdateEntity={operations.updateEntity}
          onCreateEntity={operations.createEntity}
          onError={onError}
          owner={email}
        />
      )}
      {children}
    </RemoteDataContext.Provider>
  );
}

RemoteDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
  email: PropTypes.string,
  isProd: PropTypes.bool.isRequired,
};

RemoteDataProvider.defaultProps = {
  email: undefined,
};

export default RemoteDataProvider;
